import { ConnectionModeEnum, type ConnectionStatusContextProps, MarketAccountStatusEnum } from '@talos/kyoko';
import { type AvailabilityCondition, AvailabilityEnum } from '../types';

export interface GetMarketAccountActiveConditionParams {
  /**
   * Tells the condition whether or not it should return AvailabilityEnum.Disabled if the MarketAccount.Status = Disabled, which means "Trading Disabled"
   * This should be true for the order form case (trading), but be false for non-trading cases such as just viewing market data. If false, we'll just raise a warning to notify the user.
   */
  disableIfTradingDisabled: boolean;
  /** For extra disambiguation of _why_ the market account could be offline, you can pass in this piece of reference data */
  marketCredentialsWithConnectionStatusByMarketAccountName?: ConnectionStatusContextProps['marketCredentialsWithConnectionStatusByMarketAccountName'];
}

/**
 * A AvailabilityCondition which checks if the item's market account status is Active.
 *
 * If there is no market account on the item, the condition passes.
 */
export function getMarketAccountActiveCondition({
  disableIfTradingDisabled,
  marketCredentialsWithConnectionStatusByMarketAccountName,
}: GetMarketAccountActiveConditionParams): AvailabilityCondition {
  return {
    id: 'isMarketAccountActive',
    ready: true,
    condition: (market, marketAccount) => {
      if (!marketAccount) {
        return { availability: AvailabilityEnum.Ok };
      }

      const ok = marketAccount.Status === MarketAccountStatusEnum.Active;
      if (ok) {
        return { availability: AvailabilityEnum.Ok };
      }

      // Disabled state of a Market Account is specifically for TRADING being disabled
      if (marketAccount.Status === MarketAccountStatusEnum.Disabled) {
        return {
          availability: disableIfTradingDisabled ? AvailabilityEnum.Disabled : AvailabilityEnum.Warning,
          render: () => 'Trading disabled for Market Account',
        };
      }

      // Else we are Inactive. We try to disambiguate why. One reason is that the credential might be disabled. If so,
      // display that as the reason.
      const credential = marketCredentialsWithConnectionStatusByMarketAccountName?.get(marketAccount.Name)?.credential;
      if (credential && credential.Mode === ConnectionModeEnum.Down) {
        // We also _always_ disable the market account for selection if the credential is down. Product ask.
        // Might need to break this part of the condition out in the future because this is a bit messy.
        return { availability: AvailabilityEnum.Disabled, render: () => 'Credential disabled' };
      }

      // Else we do not know, lets just state that the Account is Inactive.
      return { availability: AvailabilityEnum.Disabled, render: () => 'Market Account is Inactive' };
    },
  };
}
